/**
 * LightboxCraft DIY — Ayaklı Lightbox size / battery / print + file upload
 */
(() => {
  const t = (i18n, key, fallback) =>
    (i18n && i18n[key] != null && String(i18n[key]).trim() !== '' && !String(i18n[key]).startsWith('translation missing'))
      ? i18n[key]
      : fallback;

  const cartAddUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart/add.js`;
  };

  const norm = (s) =>
    String(s || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();

  const includesAny = (hay, needles) => {
    if (!needles || !needles.length) return false;
    const h = norm(hay);
    return needles.some((n) => h.includes(norm(n)));
  };

  const excludesAny = (hay, needles) => {
    if (!needles || !needles.length) return true;
    const h = norm(hay);
    return !needles.some((n) => h.includes(norm(n)));
  };

  // "dış" / "kırmızı" keep a dotless ı after NFD, so they are "dıs" / "kırmızı", not "dis" / "kirmizi".
  const taggedOutdoor = (hay) => /(outdoor|d[iı]s mekan)/.test(hay);
  const taggedOutdoorMedia = (hay) => /(outdoor|d[iı]s mekan|k[iı]rm[iı]z[iı])/.test(hay);

    const formatMoney = (cents, _moneyFormat) => {
    if (cents == null || cents === '') return '—';
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    const whole = Math.round(n / 100);
    return `${whole.toLocaleString('tr-TR')} ₺`;
  };

  const moneyOf = (variant, moneyFormat) => {
    if (!variant) return '—';
    return formatMoney(variant.price, moneyFormat);
  };

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  const notifyHorizonCart = async (addResponse, lines) => {
    const sectionIds = cartSectionIds();
    let cartJson = null;
    try {
      cartJson = await fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
    } catch (_) {}

    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines || [],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: cartJson
          ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson)
          : undefined,
        detail: {
          items: cartJson?.items || addResponse?.items || [],
          source: 'lightbox-craft',
          itemCount: cartJson?.item_count,
          sections: addResponse?.sections || {},
          didError: false,
        },
      });
    } catch (err) {
      console.warn('[LightboxCraft] cart event fallback', err);
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartJson || addResponse, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartJson || addResponse, bubbles: true }));
      // Force drawer section re-render if possible
      sectionIds.forEach((id) => {
        const el = document.querySelector(`cart-items-component[data-section-id="${id}"]`);
        if (el && typeof el.fetchCartData === 'function') {
          try { el.fetchCartData(); } catch (_) {}
        }
      });
    }

    // Open cart drawer when available
    const drawer = document.querySelector('cart-drawer-component, cart-drawer, theme-drawer[data-cart-drawer]');
    if (drawer) {
      if (typeof drawer.open === 'function') drawer.open();
      else if (typeof drawer.show === 'function') drawer.show();
      else drawer.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }
  };

  class LightboxCraft extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfgEl = document.getElementById(this.dataset.configId || '');
      if (!cfgEl) return;
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        console.error('[LightboxCraft] bad config', e);
        return;
      }

      this.i18n = this.config.i18n || {};
      this.variants = this.config.variants || [];
      const allOptions = this.config.options || [];
      this.options = allOptions.filter((opt) => {
        const base = this._findVariant(opt.baseContains, opt.baseExcludes || []);
        return Boolean(base) && this._matchesProductFace(opt);
      });
      if (!this.options.length) this.options = allOptions;
      this.productImages = this.config.productImages || [];
      this.moneyFormat = this.config.moneyFormat || '{{amount}} ₺';

      this.environment = this.options[0]?.environment || 'indoor';
      this.optionId = null;
      this.wantBattery = false;
      this.wantPrint = false;
      this.fileFront = null;
      this.fileBack = null;

      this._cacheDom();
      this._initPrintSource();
      this._bind();
      this._placePrintAfterCtas();
      this._markProductHosts();
      this._interceptCart();
      this._renderEnvironments();
      this._selectDefault();
    }

    _cacheDom() {
      this.envEl = this.querySelector('[data-lb-environments]');
      this.sizesEl = this.querySelector('[data-lb-sizes]');
      this.batteryBtn = this.querySelector('[data-lb-battery-btn]');
      this.batteryLabel = this.querySelector('[data-lb-battery-label]');
      this.batteryMeta = this.querySelector('[data-lb-battery-meta]');
      this.batteryPriceEl = this.querySelector('[data-lb-battery-price]');
      this.printBtn = this.querySelector('[data-lb-print-btn]');
      this.printMeta = this.querySelector('[data-lb-print-meta]');
      this.printPriceEl = this.querySelector('[data-lb-print-price]');
      this.uploadEl = this.querySelector('[data-lb-upload]');
      this.printGuideEl = this.querySelector('[data-lb-print-guide]');
      this.fileFrontInput = this.querySelector('[data-lb-file-front]');
      this.fileBackInput = this.querySelector('[data-lb-file-back]');
      this.fileFrontName = this.querySelector('[data-lb-file-front-name]');
      this.fileBackName = this.querySelector('[data-lb-file-back-name]');
      // Legacy single-file fallback (older markup)
      this.fileInput = this.querySelector('[data-lb-file]');
      this.fileNameEl = this.querySelector('[data-lb-file-name]');
      this.printWarning = this.querySelector('[data-lb-print-warning]');
      this.printStepEl = this.querySelector('[data-lb-print-step]');
      this.descEl = this.querySelector('[data-lb-desc]');
      this.basePriceEl = this.querySelector('[data-lb-base-price]');
      this.batteryRow = this.querySelector('[data-lb-battery-row]');
      this.batteryLine = this.querySelector('[data-lb-battery-line]');
      this.batteryStateEl = this.querySelector('[data-lb-battery-state]');
      this.printRow = this.querySelector('[data-lb-print-row]');
      this.printLine = this.querySelector('[data-lb-print-line]');
      this.printStateEl = this.querySelector('[data-lb-print-state]');
      this.totalEl = this.querySelector('[data-lb-total]');
      this.statusEl = this.querySelector('[data-lb-status]');
    }

    _bind() {
      this.batteryBtn?.addEventListener('click', () => {
        this.wantBattery = !this.wantBattery;
        this._sync();
      });
      this.printBtn?.addEventListener('click', () => {
        const next = !this.wantPrint;
        this.wantPrint = next;
        if (!next) {
          this._clearFiles();
          this.printSrc?.reset();
          this._sync();
          return;
        }
        this._sync();
        this._openPrintUpload();
      });
      this._bindFileUploads();
    }

    _bindFileUploads() {
      const bindInput = (input, onFile) => {
        if (!input) return;
        input.addEventListener('change', () => onFile(input.files?.[0] || null));
        const drop = input.closest('label, .craft-print__drop');
        if (!drop) return;
        ['dragenter', 'dragover'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.remove('is-dragover');
          });
        });
        drop.addEventListener('drop', (e) => {
          const file = e.dataTransfer?.files?.[0];
          if (!file) return;
          try {
            const dt = new DataTransfer();
            dt.items.add(file);
            input.files = dt.files;
          } catch (_) {}
          onFile(file);
        });
      };

      bindInput(this.fileFrontInput, (file) => this._setFront(file));
      bindInput(this.fileBackInput, (file) => this._setBack(file));
      bindInput(this.fileInput, (file) => this._setFront(file));
    }

    _initPrintSource() {
      const pdp = window.HarfmixCraftPdp;
      this.printSrc =
        pdp && pdp.createPrintSource
          ? pdp.createPrintSource(this, 'lb', {
              onService: () => this._clearFiles(),
              onChange: () => this._sync(),
            })
          : { source: 'upload', isService: false, noteValue: () => '', reset() {}, sync() {} };
    }

    /** Print add-on lives BELOW the buy buttons (same as standbox-craft.js). */
    _placePrintAfterCtas() {
      const step = this.printStepEl || this.printBtn?.closest('.craft-print-step');
      if (!step) return;
      const tryPlace = () => {
        const pdp = window.HarfmixCraftPdp;
        if (pdp && pdp.placePrintAfterCtas) {
          pdp.placePrintAfterCtas(this, step);
          return true;
        }
        return false;
      };
      if (tryPlace()) return;
      [50, 200, 600, 1500].forEach((ms) => setTimeout(tryPlace, ms));
    }

    _markProductHosts() {
      document.querySelectorAll('product-info, .product, .product-information').forEach((el) => {
        el.classList.add('product--lightboxcraft');
        const boot = () => { if (window.HarfmixCraftPdp) { window.HarfmixCraftPdp.init({ quoteOnly: false }); return true; } return false; };
        if (!boot()) [50, 200, 600, 1500].forEach((ms) => setTimeout(boot, ms));
      });
    }

    _interceptCart() {
      const isOurAtcButton = (el) => {
        if (!(el instanceof Element)) return false;
        const btn = el.closest('button[type="submit"], button[name="add"], [name="add"]');
        if (!btn) return false;
        if (btn.closest('lightbox-craft, .lightbox-craft')) return false;
        if (
          document.body.contains(this) &&
          btn.closest('sticky-add-to-cart, .sticky-add-to-cart, [data-sticky-add-to-cart], product-form, .product-form, form[action*="/cart/add"]')
        ) {
          return true;
        }
        return false;
      };

      const isOurCartForm = (form) => {
        if (!(form instanceof HTMLFormElement)) return false;
        if (!form.action || !/\/cart\/add/.test(form.action)) return false;
        if (form.closest('lightbox-craft')) return false;
        return document.body.contains(this);
      };

      const guard = (event) => {
        const target = event.target;
        if (event.type === 'submit') {
          const form = target instanceof HTMLFormElement ? target : target.closest?.('form');
          if (!isOurCartForm(form)) return;
          event.preventDefault();
          event.stopPropagation();
          this._addToCart();
          return;
        }
        if (!isOurAtcButton(target)) return;
        event.preventDefault();
        event.stopPropagation();
        this._addToCart();
      };

      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
    }


    _productFace() {
      const title = this.config.productTitle || '';
      if (includesAny(title, ['çift taraflı', 'cift tarafli', 'çift yüz', 'cift yuz'])) return 'cift';
      if (includesAny(title, ['tek taraflı', 'tek tarafli', 'tek yüz', 'tek yuz'])) return 'tek';
      return null;
    }

    _matchesProductFace(opt) {
      if (!opt || !opt.face) return true;
      const face = this._productFace();
      if (!face) return true;
      return opt.face === face;
    }

    _optionHasBase(opt) {
      if (!opt) return false;
      if (!this._matchesProductFace(opt)) return false;
      return Boolean(this._findVariant(opt.baseContains, opt.baseExcludes));
    }

    _optionsForEnv(env) {
      return this.options.filter(
        (o) => o.environment === env && this._optionHasBase(o)
      );
    }

    _findVariant(contains, excludes) {
      return (
        this.variants.find(
          (v) => includesAny(v.title, contains) && excludesAny(v.title, excludes || [])
        ) || null
      );
    }

    _currentOption() {
      return this.options.find((o) => o.id === this.optionId) || null;
    }

    _imageHaystack(img) {
      if (!img) return '';
      const src = String(img.src || img.url || '');
      let file = '';
      try {
        file = decodeURIComponent((src.split('?')[0] || '').split('/').pop() || '');
      } catch (_) {
        file = src;
      }
      return [img.alt, file, src, img.mediaId].filter(Boolean).join(' ');
    }

    _scoreHay(hayRaw, opt) {
      if (!opt) return 0;
      const hay = norm(hayRaw);
      if (!hay) return 0;
      const excludes = opt.imageExclude || [];
      if (excludes.length && includesAny(hay, excludes)) return 0;

      const matches = opt.imageMatch || [];
      let score = 0;
      matches.forEach((needle, i) => {
        const n = norm(needle);
        if (!n) return;
        if (hay.includes(n)) score += Math.max(1, matches.length - i);
      });

      if (opt.environment === 'outdoor') {
        // Outdoor must be explicitly tagged — never fall back to indoor size photos.
        if (!taggedOutdoorMedia(hay)) return 0;
        score += 10;
      } else if (taggedOutdoor(hay)) {
        return 0;
      }
      return score;
    }

    _scoreImage(img, opt) {
      if (!img || !opt) return 0;
      return this._scoreHay(this._imageHaystack(img), opt);
    }

    _matchingImages(opt) {
      if (!opt || !this.productImages.length) return [];
      const scored = this.productImages
        .map((img) => ({ img, score: this._scoreImage(img, opt) }))
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score);
      return scored.map((x) => x.img);
    }

    _resolveImage(opt) {
      const hits = this._matchingImages(opt);
      if (hits[0]?.url) return hits[0].url;
      if (this.productImages[0]?.url) return this.productImages[0].url;
      return opt?.imageAsset || '';
    }

    _galleryMediaItems(gallery) {
      // Grid presentation uses <li class="product-media-container">; carousel uses slideshow-slide
      const gridItems = Array.from(gallery.querySelectorAll('.media-gallery__grid > li, [ref="media[]"]'));
      if (gridItems.length) return gridItems;
      return Array.from(gallery.querySelectorAll('slideshow-slide'));
    }

    _syncProductMedia(opt) {
      const gallery = document.querySelector('.product-information__media media-gallery, media-gallery');
      if (!gallery || !opt) return;

      const items = this._galleryMediaItems(gallery);
      if (!items.length) return;

      const scored = items.map((item, index) => {
        const mediaEl = item.querySelector('[data-media-id]') || item;
        const mediaId = String(mediaEl.getAttribute('data-media-id') || '');
        const img = item.querySelector('img.product-media__image, img');
        const alt = img?.getAttribute('alt') || '';
        const src = (img?.currentSrc || img?.getAttribute('src') || '').split('?')[0];
        const hay = [alt, src, mediaId].filter(Boolean).join(' ');
        return { item, index, score: this._scoreHay(hay, opt), alt };
      });

      let firstVisibleIndex = -1;
      scored.forEach(({ item, index, score }) => {
        // Strict: only alt/keyword matches for the selected option
        const match = score > 0;
        item.hidden = !match;
        item.toggleAttribute('hidden', !match);
        item.setAttribute('aria-hidden', match ? 'false' : 'true');
        item.classList.toggle('lb-media-hidden', !match);
        if (match && firstVisibleIndex < 0) firstVisibleIndex = index;
      });

      // Also filter carousel slides if both grid + slideshow exist in DOM
      gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        if (items.includes(slide)) return;
        const img = slide.querySelector('img');
        const alt = img?.getAttribute('alt') || '';
        const src = (img?.currentSrc || img?.getAttribute('src') || '').split('?')[0];
        const mediaId = slide.querySelector('[data-media-id]')?.getAttribute('data-media-id') || '';
        const match = this._scoreHay([alt, src, mediaId].join(' '), opt) > 0;
        slide.hidden = !match;
        slide.classList.toggle('lb-media-hidden', !match);
      });

      const thumbs = gallery.querySelectorAll(
        'slideshow-controls button[aria-controls], .slideshow-controls__thumbnail, [ref="thumbnails"] button'
      );
      if (thumbs.length === scored.length) {
        thumbs.forEach((thumb, index) => {
          const hide = scored[index]?.item?.hidden;
          thumb.hidden = Boolean(hide);
          thumb.classList.toggle('lb-media-hidden', Boolean(hide));
        });
      }

      const selectTarget = gallery.slideshow || gallery.querySelector('slideshow-component');
      if (selectTarget && typeof selectTarget.select === 'function' && firstVisibleIndex >= 0) {
        try {
          selectTarget.select(firstVisibleIndex, undefined, { animate: false });
        } catch (_) {
          try {
            selectTarget.select(firstVisibleIndex);
          } catch (_) {}
        }
      }
    }

    _resolved() {
      const opt = this._currentOption();
      if (!opt) return {};
      const base = this._findVariant(opt.baseContains, opt.baseExcludes);
      const battery =
        opt.supportsBattery === false
          ? null
          : this._findVariant(opt.batteryContains, ['baskı', 'baski']);
      const print =
        opt.supportsPrint === false
          ? null
          : this._findVariant(opt.printContains, ['pil']);
      return { opt, base, battery, print };
    }

    _selectDefault() {
      const envs = this._availableEnvironments();
      this.environment = envs[0]?.id || this.options[0]?.environment || 'indoor';
      const list = this._optionsForEnv(this.environment);
      this.optionId = list[0]?.id || this.options[0]?.id;
      this._sync();
    }

    _availableEnvironments() {
      const seen = new Set();
      const list = [];
      this.options.forEach((opt) => {
        if (!opt.environment || seen.has(opt.environment)) return;
        if (!this._optionHasBase(opt)) return;
        seen.add(opt.environment);
        list.push({
          id: opt.environment,
          label:
            opt.environment === 'outdoor'
              ? t(this.i18n, 'outdoor', 'Dış mekan')
              : t(this.i18n, 'indoor', 'İç mekan'),
        });
      });
      return list;
    }

    _envStepEl() {
      return this.envEl?.closest('.lightbox-craft__step') || null;
    }

    _sizeStepEl() {
      return this.sizesEl?.closest('.lightbox-craft__step') || null;
    }

    _renderEnvironments() {
      if (!this.envEl) return;
      this.envEl.innerHTML = '';
      const envs = this._availableEnvironments();
      const envStep = this._envStepEl();
      if (envs.length <= 1) {
        if (envStep) envStep.hidden = true;
        return;
      }
      if (envStep) envStep.hidden = false;
      envs.forEach((env) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lightbox-craft__pill';
        btn.textContent = env.label;
        btn.setAttribute('aria-pressed', env.id === this.environment ? 'true' : 'false');
        btn.addEventListener('click', () => {
          this.environment = env.id;
          this.wantBattery = false;
          const list = this._optionsForEnv(env.id);
          this.optionId = list[0]?.id || null;
          this._sync();
        });
        this.envEl.appendChild(btn);
      });
    }

    _renderSizes() {
      if (!this.sizesEl) return;
      this.sizesEl.innerHTML = '';
      const list = this._optionsForEnv(this.environment);
      const sizeStep = this._sizeStepEl();
      if (list.length <= 1) {
        if (sizeStep) sizeStep.hidden = true;
        if (list.length === 1 && this.optionId !== list[0].id) {
          this.optionId = list[0].id;
        }
        return;
      }
      if (sizeStep) sizeStep.hidden = false;
      list.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'lightbox-craft__pill';
        btn.textContent = opt.label;
        btn.title = opt.standLabel || '';
        btn.setAttribute('aria-pressed', opt.id === this.optionId ? 'true' : 'false');
        btn.addEventListener('click', () => {
          this.optionId = opt.id;
          this.wantBattery = false;
          this._sync();
        });
        this.sizesEl.appendChild(btn);
      });
    }

    _setFront(file) {
      this.fileFront = file || null;
      if (this.fileFrontInput && !file) this.fileFrontInput.value = '';
      if (this.fileInput && !file) this.fileInput.value = '';
      this._sync();
    }

    _setBack(file) {
      this.fileBack = file || null;
      if (this.fileBackInput && !file) this.fileBackInput.value = '';
      this._sync();
    }

    _hasPrintFile() {
      return Boolean(this.fileFront || this.fileBack);
    }

    _openPrintUpload({ scroll = true } = {}) {
      this.wantPrint = true;
      if (this.uploadEl) this.uploadEl.hidden = false;
      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', 'true');
      if (scroll) {
        const target = this.uploadEl || this.printStepEl || this.printBtn?.closest('.craft-print-step') || this.printBtn;
        if (target) {
          requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          });
        }
      }
      if (this.printSrc?.isService) {
        this.printSrc.noteEl?.focus?.({ preventScroll: true });
      } else {
        (this.fileFrontInput || this.fileInput)?.focus?.({ preventScroll: true });
      }
    }

    _clearFiles() {
      this._setFront(null);
      this._setBack(null);
    }

    _setStatus(msg, tone) {
      if (!this.statusEl) return;
      if (!msg) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = msg;
      this.statusEl.dataset.tone = tone || 'success';
    }

    _money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    _sync() {
      this.dataset.env = this.environment;
      this._renderEnvironments();
      this._renderSizes();

      const { opt, base, battery, print } = this._resolved();
      const i18n = this.i18n;

      if (this.descEl) this.descEl.innerHTML = opt?.description || '';

      this._syncProductMedia(opt);

      const supportsBattery = Boolean(opt && opt.supportsBattery !== false && battery);
      if (this.batteryBtn) {
        this.batteryBtn.hidden = !supportsBattery;
        this.batteryBtn.setAttribute('aria-pressed', this.wantBattery && supportsBattery ? 'true' : 'false');
        if (this.batteryLabel) {
          this.batteryLabel.textContent = opt?.batteryLabel || t(i18n, 'battery', 'Lityum pil');
        }
        if (this.batteryMeta) {
          this.batteryMeta.textContent = this.wantBattery
            ? t(i18n, 'batteryOn', 'Eklendi — toplama dahil')
            : t(i18n, 'batteryOff', 'Opsiyonel — prize takarak da çalışır');
        }
        if (this.batteryPriceEl) {
          this.batteryPriceEl.textContent =
            battery ? `+ ${moneyOf(battery, this.moneyFormat)}` : '';
        }
      }
      if (!supportsBattery) this.wantBattery = false;
      const batteryStep = this.batteryBtn?.closest('.lightbox-craft__step');
      if (batteryStep) batteryStep.hidden = !supportsBattery;

      const supportsPrint = !opt || opt.supportsPrint !== false;
      const printIncluded = Boolean(opt?.printIncluded);
      const printQty = this._printQty(opt, print);
      if (this.printBtn) {
        this.printBtn.hidden = !supportsPrint;
        this.printBtn.setAttribute('aria-pressed', this.wantPrint && supportsPrint ? 'true' : 'false');
      }
      if (this.printStepEl) this.printStepEl.hidden = !supportsPrint;
      if (this.printMeta) {
        const area = opt?.printArea
          ? `${t(i18n, 'printSizeLabel', 'Baskı alanı')}: ${opt.printArea}`
          : '';
        if (this.wantPrint && supportsPrint) {
          const dualNote =
            printQty > 1
              ? t(i18n, 'printDualCharge', '2 adet (çift yüz) ücreti uygulanır')
              : t(i18n, 'addonAdded', 'Eklendi — toplama dahil');
          const note = this.printSrc?.isService
            ? t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti')
            : dualNote;
          this.printMeta.textContent = area ? `${note} · ${area}` : note;
        } else {
          this.printMeta.textContent = area || t(i18n, 'printOff', 'Baskı dahil değil');
        }
      }
      if (this.printPriceEl) {
        if (printIncluded) {
          this.printPriceEl.textContent = t(i18n, 'printIncluded', 'Dahil');
        } else if (this.wantPrint && supportsPrint && print) {
          this.printPriceEl.textContent = this._printMoneyLabel(print, printQty, this.moneyFormat);
        } else {
          this.printPriceEl.textContent = '—';
        }
      }
      if (this.uploadEl) this.uploadEl.hidden = !(this.wantPrint && supportsPrint);
      this.printSrc?.sync(this.wantPrint && supportsPrint);
      if (this.printGuideEl) {
        const guideUrl = opt?.printGuideUrl || '';
        if (guideUrl) {
          this.printGuideEl.href = guideUrl;
          this.printGuideEl.hidden = false;
        } else {
          this.printGuideEl.removeAttribute('href');
          this.printGuideEl.hidden = true;
        }
      }

      if (this.printWarning) this.printWarning.hidden = true;

      if (this.fileFrontName) {
        this.fileFrontName.hidden = !this.fileFront;
        this.fileFrontName.textContent = this.fileFront?.name || '';
      }
      if (this.fileBackName) {
        this.fileBackName.hidden = !this.fileBack;
        this.fileBackName.textContent = this.fileBack?.name || '';
      }
      if (this.fileNameEl) {
        this.fileNameEl.hidden = !this.fileFront;
        this.fileNameEl.textContent = this.fileFront?.name || '';
      }
      // Single-sided options: hide the back dropzone (standbox-style dual markup still used).
      const backDrop = this.fileBackInput?.closest('label, .craft-print__drop');
      if (backDrop) backDrop.hidden = printQty <= 1 && (Number(opt?.printSides) || 1) <= 1;

      const baseCents = Number(base?.price) || 0;
      const batteryCents = this.wantBattery && supportsBattery ? Number(battery?.price) || 0 : 0;
      const printCents =
        this.wantPrint && supportsPrint && !printIncluded
          ? (Number(print?.price) || 0) * Math.max(1, printQty || 1)
          : 0;
      const totalCents = baseCents + batteryCents + printCents;

      if (this.basePriceEl) this.basePriceEl.textContent = moneyOf(base, this.moneyFormat);

      // Battery line: visible whenever battery exists for this option
      if (this.batteryRow) {
        const showBatteryRow = Boolean(supportsBattery);
        this.batteryRow.hidden = !showBatteryRow;
        this.batteryRow.classList.toggle('is-added', Boolean(this.wantBattery && supportsBattery));
        if (this.batteryStateEl) {
          this.batteryStateEl.textContent = this.wantBattery && supportsBattery
            ? t(i18n, 'addonAdded', 'Eklendi')
            : t(i18n, 'addonNotAdded', 'Eklenmedi');
        }
        if (this.batteryLine) {
          this.batteryLine.textContent = this.wantBattery && supportsBattery
            ? `+ ${moneyOf(battery, this.moneyFormat)}`
            : moneyOf(battery, this.moneyFormat);
        }
      }

      // Print line: visible whenever print is available
      if (this.printRow) {
        const showPrintRow = Boolean(supportsPrint);
        this.printRow.hidden = !showPrintRow;
        const printAdded = Boolean(this.wantPrint && supportsPrint) || printIncluded;
        this.printRow.classList.toggle('is-added', printAdded);
        if (this.printStateEl) {
          if (printIncluded) {
            this.printStateEl.textContent = t(i18n, 'printIncluded', 'Dahil');
          } else {
            this.printStateEl.textContent = this.wantPrint && supportsPrint
              ? (printQty > 1
                  ? t(i18n, 'printDualCharge', '2 adet (çift yüz) ücreti uygulanır')
                  : t(i18n, 'addonAdded', 'Eklendi'))
              : t(i18n, 'addonNotAdded', 'Eklenmedi');
          }
        }
        if (this.printLine) {
          if (printIncluded) {
            this.printLine.textContent = t(i18n, 'printIncluded', 'Dahil');
          } else if (this.wantPrint && supportsPrint && print) {
            this.printLine.textContent = this._printMoneyLabel(print, printQty, this.moneyFormat);
          } else {
            this.printLine.textContent = '—';
          }
        }
      }

      if (this.totalEl) this.totalEl.textContent = this._money(totalCents);

      if (base?.id) {
        const input = document.querySelector(
          `input[name="id"][value="${base.id}"], form[action*="/cart/add"] [name="id"]`
        );
        if (input) {
          input.value = String(base.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }


    _printQty(opt, print) {
      if (!opt || opt.printIncluded) return 0;
      const sides = Number(opt.printSides) || 1;
      const title = String(print?.title || '').toLocaleLowerCase('tr-TR');
      // Variant already priced as "2 adet" → qty 1
      if (sides > 1 && /2\s*adet/.test(title)) return 1;
      return Math.max(1, sides);
    }

    _printMoneyLabel(print, qty, moneyFormat) {
      if (!print) return '';
      const unit = Number(print.price) || 0;
      const total = unit * Math.max(1, qty || 1);
      const money = formatMoney(total, moneyFormat);
      if (qty > 1) return `+ ${money} (2 adet)`;
      return `+ ${money}`;
    }

    async _addToCart() {
      const i18n = this.i18n;
      const { opt, base, battery, print } = this._resolved();
      const printIncluded = Boolean(opt?.printIncluded);

      if (!base?.id || !base.available) {
        this._setStatus(t(i18n, 'variantMissing', 'Varyant bulunamadı.'), 'error');
        return;
      }

      const isService = Boolean(this.printSrc?.isService);
      if (this.wantPrint && !isService && !this._hasPrintFile()) {
        if (this.printWarning) this.printWarning.hidden = false;
        this._openPrintUpload();
        this._setStatus(
          t(i18n, 'printRequired', 'En az bir yüz için baskı dosyası yükleyin veya HarfMix hizmetini seçin.'),
          'error'
        );
        return;
      }

      const sharedProps = {
        [t(i18n, 'propEnvironment', 'Ortam')]:
          this.environment === 'outdoor' ? t(i18n, 'outdoor', 'Dış mekan') : t(i18n, 'indoor', 'İç mekan'),
        [t(i18n, 'propSize', 'Ölçü')]: opt?.label || '',
        [t(i18n, 'propStand', 'Ayak Tipi')]: opt?.standLabel || opt?.standType || '',
        [t(i18n, 'propBattery', 'Lityum Pil')]:
          this.wantBattery ? t(i18n, 'yes', 'Evet') : t(i18n, 'no', 'Hayır'),
        [t(i18n, 'propPrint', 'Görsel Baskı')]:
          this.wantPrint || printIncluded ? t(i18n, 'yes', 'Evet') : t(i18n, 'no', 'Hayır'),
      };

      const fd = new FormData();
      let index = 0;

      const appendItem = (variantId, props, files, quantity = 1) => {
        fd.append(`items[${index}][id]`, String(variantId));
        fd.append(`items[${index}][quantity]`, String(Math.max(1, quantity || 1)));
        Object.entries(props).forEach(([k, v]) => {
          if (v == null || v === '') return;
          fd.append(`items[${index}][properties][${k}]`, String(v));
        });
        (files || []).forEach(({ key, file }) => {
          if (!file) return;
          fd.append(`items[${index}][properties][${key}]`, file, file.name);
        });
        index += 1;
      };

      const printFiles =
        this.wantPrint && !isService
          ? [
              { key: t(i18n, 'propPrintFront', 'Ön Yüz Baskı'), file: this.fileFront },
              { key: t(i18n, 'propPrintBack', 'Arka Yüz Baskı'), file: this.fileBack },
            ]
          : [];
      const baseFiles = printIncluded || !print?.id ? printFiles : [];
      appendItem(
        base.id,
        {
          ...sharedProps,
          [t(i18n, 'propType', 'Ürün Tipi')]: 'Ayaklı Lightbox',
        },
        baseFiles
      );

      if (this.wantBattery && battery?.id) {
        appendItem(battery.id, {
          ...sharedProps,
          [t(i18n, 'propType', 'Ürün Tipi')]: opt?.batteryLabel || 'Lityum Pil',
          _lb_addon: 'battery',
        });
      }

      const printQty = this._printQty(opt, print);
      if (this.wantPrint && !printIncluded) {
        if (print?.id) {
          appendItem(
            print.id,
            {
              ...sharedProps,
              [t(i18n, 'propType', 'Ürün Tipi')]:
                printQty > 1 ? 'Özel Dijital Baskı (2 adet)' : 'Özel Dijital Baskı',
              [t(i18n, 'propPrint', 'Görsel Baskı')]: opt?.printArea || t(i18n, 'yes', 'Evet'),
              [t(i18n, 'propPrintBilling', 'Baskı ücreti')]:
                printQty > 1
                  ? t(i18n, 'printBillingTwo', '2 adet — çift yüz (tek yüz yüklense bile)')
                  : t(i18n, 'printBillingOne', '1 adet'),
              [t(i18n, 'propPrintSource', 'Baskı tercihi')]: isService
                ? t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti')
                : t(i18n, 'printSourceUploadValue', 'Dosya yükleme'),
              ...(this.printSrc?.noteValue()
                ? { [t(i18n, 'propPrintNote', 'Baskı notu')]: this.printSrc.noteValue() }
                : {}),
              _lb_addon: 'print',
            },
            isService ? [] : printFiles,
            printQty
          );
        } else {
          this._setStatus(t(i18n, 'variantMissing', 'Baskı varyantı bulunamadı.'), 'error');
          return;
        }
      }

      const sectionIds = cartSectionIds();
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));

      this._setStatus(t(i18n, 'adding', 'Ekleniyor…'), 'success');
      try {
        const res = await fetch(cartAddUrl(), {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || t(i18n, 'addError', 'Sepete eklenemedi.'));
        }
        this._setStatus(t(i18n, 'added', 'Sepete eklendi.'), 'success');

        const lines = [];
        // Reconstruct lines from what we appended
        // (FormData order: base, optional battery, optional print)
        lines.push({ merchandiseId: String(base.id), quantity: 1 });
        if (this.wantBattery && battery?.id) {
          lines.push({ merchandiseId: String(battery.id), quantity: 1 });
        }
        if (this.wantPrint && !printIncluded && print?.id) {
          lines.push({ merchandiseId: String(print.id), quantity: Math.max(1, printQty || 1) });
        }

        await notifyHorizonCart(data, lines);

        if (window.publish && window.PUB_SUB_EVENTS?.cartUpdate) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lightbox-craft', cartData: data });
          } catch (_) {}
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data, bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data, bubbles: true }));
      } catch (err) {
        console.error('[LightboxCraft]', err);
        this._setStatus(err.message || t(i18n, 'addError', 'Sepete eklenemedi.'), 'error');
      }
    }
  }

  if (!customElements.get('lightbox-craft')) {
    customElements.define('lightbox-craft', LightboxCraft);
  }
})();
